import { h, defineComponent, computed, ref, watch } from 'vue';
// @ts-ignore
import { BAutocomplete } from 'buefy/dist/esm/autocomplete';
import { SelectOption, SelectOptions, SetterCallback } from '@/ui/types';

export interface AutocompleteProps<T = number> {
  value: T | null;
  placeholder: string;
  data: SelectOptions<T>;
  maxHeight?: number;
  disabled: boolean;
  limit?: number;
}

export const Autocomplete = defineComponent<AutocompleteProps>({
  name: 'Autocomplete',
  props: ['value', 'placeholder', 'data', 'maxHeight', 'disabled', 'limit'],
  setup(props, context) {
    type T = typeof props.value;

    const filter = ref('');

    watch(() => props.value, () => {
      if (!props.value) {
        filter.value = '';
        return;
      }

      filter.value = props.data.find(item => item.value === props.value)?.label || '';
    }, { immediate: true });

    const filteredOptions = computed(() => {
      const limit = props.limit || props.data.length;
      if (!filter.value) {
        return props.data.slice(0, limit);
      }

      //we don't use Array.filter() because we want to stop filtering after we reach the limit
      const filtered = [];
      const searchString = filter.value.toLowerCase();

      for (const item of props.data) {
        if (String(item.label).toLowerCase().includes(searchString)) {
          filtered.push(item);
        }

        if (filtered.length >= limit) {
          break;
        }
      }

      return filtered;
    });

    const hasItemSelected = (value: T) => value !== null;

    return function render() {
      return h(BAutocomplete, {
        props: {
          data: filteredOptions.value,
          field: 'label',
          maxHeight: props.maxHeight,
          openOnFocus: true,
          value: filter.value,
        },
        class: {
          'has-item-selected': hasItemSelected(props.value),
        },
        attrs: {
          disabled: props.disabled,
          placeholder: props.placeholder,
        },
        scopedSlots: {
          default: ({ option }: { option: SelectOption }) => {
            return h('span', {
              class: {
                'is-disabled': option.disabled,
              },
            }, option.label);
          },
        },
        on: {
          input: (newValue: string) => {
            filter.value = newValue;

            if (!newValue) {
              context.emit('input', null);
            }
          },
          select: (item: SelectOption|null) => {
            if (item?.disabled) {
              return;
            }
            context.emit('input', item ? item.value : null);
          },
        },
      });
    };
  },
});

export function autocomplete<T>(props: AutocompleteProps<T>, input: SetterCallback<T>) {
  return h(Autocomplete, {
    props,
    on: { input },
  });
}
